import React, { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import HourglassIcon from '@/assets/images/desk/hourglass.svg';
import ClockIcon from '@/assets/images/desk/clock.svg';
import { ProgressBars } from './ProgressBars';
import { LocalStorageKeys } from '@/constants/localStorage';
import { useTimePanelController } from './hooks/useTimePanelController';
import { useDeskPcViewport } from '@/hooks/useDeskPcViewport';
import Tooltip from '@/components/Tooltip';

const TimeProgressPanel: FC = () => {
    const { t } = useTranslation('timePanel');
    const isPcDesk = useDeskPcViewport();

    const { containerRef, fontSize, isCountdown, toggleTimeMode, progress, lifeProgress } =
        useTimePanelController({
            storageKey: LocalStorageKeys.PROGRESS_PANEL_MODE,
        });

    const [layoutCompact, setLayoutCompact] = React.useState(false);
    React.useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const measure = () => {
            const h = el.getBoundingClientRect().height;
            setLayoutCompact(h > 0 && h < 92 && !isPcDesk);
        };
        measure();
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [containerRef, isPcDesk]);

    const toggleLabel = isCountdown ? t('toggle.toNormal') : t('toggle.toCountdown');
    const iconAlt = isCountdown ? t('toggle.clockAlt') : t('toggle.hourglassAlt');
    const navigate = useNavigate();

    const handleAddLifeBar = useCallback(() => {
        navigate('/settings#birth-year');
    }, [navigate]);

    return (
        <div
            ref={containerRef}
            style={{ fontSize: `${fontSize}px` }}
            className={`w-full h-full flex flex-col justify-center items-stretch relative ${layoutCompact ? 'pt-0.5 pb-0' : 'px-3'}`}
        >
            <Tooltip content={toggleLabel}>
                <button
                    className={`absolute top-0 ${layoutCompact ? 'right-1' : 'right-0'} inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300`}
                    onClick={toggleTimeMode}
                    aria-label={toggleLabel}
                    type="button"
                >
                    {isCountdown ? (
                        <img src={ClockIcon} alt={iconAlt} className="filter invert-[0.5]" />
                    ) : (
                        <img src={HourglassIcon} alt={iconAlt} className="filter invert-[0.5]" />
                    )}
                </button>
            </Tooltip>
            <div
                className={`flex-1 flex min-w-0 w-full ${
                    layoutCompact ? 'items-center justify-start' : 'items-center justify-center'
                }`}
            >
                <div
                    className={
                        layoutCompact
                            ? 'w-[calc(100%-3.25rem)] max-w-full min-w-0 flex items-center justify-start overflow-x-auto overflow-y-hidden pl-2'
                            : 'w-full flex justify-center min-w-0'
                    }
                >
                    <ProgressBars
                        progress={progress}
                        isCountdown={isCountdown}
                        lifeProgress={lifeProgress}
                        onAddLifeBar={handleAddLifeBar}
                        compact={layoutCompact}
                    />
                </div>
            </div>
        </div>
    );
};

export default TimeProgressPanel;
